/**
 * StudioImportDialog — import CAD/shapefile/KMZ/GeoJSON geometry straight
 * into the Test-Fit Studio and ASSIGN each row a role: reference overlay,
 * no-build exclusion, acoustic barrier (required height), custom component
 * (road corridors prompt a required width), or an equipment pin (centroid +
 * longest-edge bearing). Runs the same shared import pipeline as the Site
 * Map dialog; role suggestions come from layer names/categories and every
 * geometry-affecting assignment lands through ONE composed schemaVersion-2
 * patch. Reference rows commit through the proven Site-Map merge path.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FileUp, Loader2, UploadCloud, X } from 'lucide-react';
import { toast } from 'sonner';

import { useStore } from '../../../../store';
import type { SiteEntity, SiteLayer } from '../../../../types/site';
import type {
  GenerationLayoutConfiguration,
} from '../../../../types/generationImpact';
import type { GenerationParcelTestFitResult } from '../../../../lib/generationImpact/generationLayoutGeometry';
import {
  cadPointToLatLng,
  georeferenceFromLayer,
  latLngToCadPoint,
} from '../../../../lib/cad/cadImport';
import { mergeImportedLayers } from '../../../../services/kmzParser';
import { preserveManagedGenerationImpactLayers } from '../../../../lib/siteLayers/managedGenerationImpactLayers';
import { GEO_IMPORT_ACCEPT } from '../../../../services/geoImport/importRouter';
import { uploadLayerImportProvenance } from '../../../../services/geoImport/layerImportProvenance';
import { parsedToSiteLayer } from '../../LayerReviewPanel';
import { useGeoImportPipeline } from '../../useGeoImportPipeline';
import {
  applyStudioImportAssignments,
  computeGenerationEquipmentOccupancy,
  equipmentNormalizationPreflight,
  rolesForGeometry,
  suggestImportRole,
  type StudioImportRole,
  type StudioImportRow,
} from './studioImportAssignments';

const ROLE_LABELS: Record<StudioImportRole['role'], string> = {
  reference: 'Reference overlay (Site Map)',
  exclusion: 'No-build exclusion zone',
  barrier: 'Acoustic barrier',
  custom: 'Custom component',
  equipment: 'Pin equipment here',
};

const FOCUSABLE_SELECTOR = [
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"]):not([tabindex="-1"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'a[href]',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

function isUnreferencedCadLayer(layer: SiteLayer | undefined): layer is SiteLayer {
  return layer?.source === 'cad-import'
    && layer.attributes?.cadGeoreferenceSource === 'site-anchor';
}

function drawingUnitLabel(metresPerUnit: unknown): string {
  if (typeof metresPerUnit !== 'number' || !Number.isFinite(metresPerUnit) || metresPerUnit <= 0) {
    return 'unknown scale';
  }
  if (Math.abs(metresPerUnit - 1) < 1e-9) return 'metres (1 m per drawing unit)';
  if (Math.abs(metresPerUnit - 0.3048) < 1e-9) return 'international feet (0.3048 m per drawing unit)';
  if (Math.abs(metresPerUnit - 1200 / 3937) < 1e-9) return 'US survey feet (0.30480061 m per drawing unit)';
  if (Math.abs(metresPerUnit - 0.001) < 1e-12) return 'millimetres (0.001 m per drawing unit)';
  return `${metresPerUnit.toPrecision(8)} m per drawing unit`;
}

/**
 * Reproject an unreferenced CAD layer from the pipeline's temporary site
 * anchor to the target-parcel centroid. Recovering each original CAD point
 * before applying the new local tangent plane preserves the declared drawing
 * scale and rotation instead of applying an approximate lat/lng translation.
 */
export function reanchorUnreferencedCadLayer(
  layer: SiteLayer,
  targetAnchor: { lat: number; lng: number },
): SiteLayer {
  const sourceGeoreference = georeferenceFromLayer(layer);
  if (!sourceGeoreference || sourceGeoreference.source !== 'site-anchor') return layer;
  const targetGeoreference = {
    ...sourceGeoreference,
    anchorLat: targetAnchor.lat,
    anchorLng: targetAnchor.lng,
    source: 'manual' as const,
  };
  return {
    ...layer,
    geometry: {
      ...layer.geometry,
      coordinates: layer.geometry.coordinates.map(point => cadPointToLatLng(
        latLngToCadPoint(point, sourceGeoreference),
        targetGeoreference,
      )),
    },
    attributes: {
      ...layer.attributes,
      cadAnchorLat: targetAnchor.lat,
      cadAnchorLng: targetAnchor.lng,
      cadGeoreferenceSource: 'manual',
      cadPlacementOrigin: 'target-parcel-centroid',
      cadDrawingUnitsConfirmed: true,
    },
  };
}

function numericDraftError(
  value: string,
  label: string,
  minimum: number,
  maximum: number,
): string | null {
  if (value.trim() === '') return `${label} is required.`;
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return `${label} must be a number.`;
  if (numeric < minimum || numeric > maximum) {
    return `${label} must be between ${minimum} and ${maximum} m.`;
  }
  return null;
}

function assignmentDraftError(
  row: StudioImportRow['layer'],
  assignment: StudioImportRole,
  index: number,
  numericDrafts: Readonly<Record<string, string>>,
): string | null {
  if (assignment.role === 'barrier') {
    const draft = numericDrafts[`barrier-height-${index}`]
      ?? (assignment.heightM == null ? '' : String(assignment.heightM));
    return numericDraftError(draft, 'Barrier height', 0.5, 50);
  }
  if (
    assignment.role === 'custom'
    && row.geometry.type === 'line'
    && assignment.kind === 'road'
  ) {
    const draft = numericDrafts[`road-width-${index}`]
      ?? (assignment.corridorWidthM == null ? '' : String(assignment.corridorWidthM));
    return numericDraftError(draft, 'Road width', 1, 100);
  }
  if (assignment.role === 'equipment' && assignment.componentId.trim() === '') {
    return 'Select equipment to pin.';
  }
  return null;
}

export interface StudioImportDialogProps {
  site: SiteEntity;
  /** Composite site/scenario identity used to reject superseded imports. */
  sessionKey: string;
  open: boolean;
  onClose: () => void;
  activeLayout: GenerationLayoutConfiguration;
  testFit: GenerationParcelTestFitResult;
  onPatchLayout: (
    patch: Partial<GenerationLayoutConfiguration>,
  ) => boolean | void | Promise<boolean | void>;
  /** Grow the physical fleet by one so a source can claim a new block slot. */
  onAddGenerationBlock?: () => void;
  /** Files handed in by drag-drop onto the studio canvas. */
  initialFiles?: File[] | null;
}

export const StudioImportDialog: React.FC<StudioImportDialogProps> = ({
  site,
  sessionKey,
  open,
  onClose,
  activeLayout,
  testFit,
  onPatchLayout,
  onAddGenerationBlock,
  initialFiles,
}) => {
  const updateSiteLayers = useStore((s) => s.updateSiteLayers);
  const {
    progress, rejections, staged, parsing, processFiles, reset: resetPipeline,
  } = useGeoImportPipeline(site.location ?? null);
  const [assignments, setAssignments] = useState<StudioImportRole[]>([]);
  const [numericDrafts, setNumericDrafts] = useState<Record<string, string>>({});
  // Per-row acknowledgement that the modeled envelope overrides the imported
  // size (only required when the source dims differ by > the confirm tolerance).
  const [confirmedNormalization, setConfirmedNormalization] = useState<Record<number, boolean>>({});
  const [applying, setApplying] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [cadUnitsConfirmed, setCadUnitsConfirmed] = useState(false);
  const [cadOriginConfirmed, setCadOriginConfirmed] = useState(false);
  const applyingRef = useRef(false);
  const applyRequestRef = useRef(0);
  const sessionKeyRef = useRef(sessionKey);
  sessionKeyRef.current = sessionKey;
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);
  const consumedInitialFiles = useRef(false);

  useEffect(() => {
    if (!open) {
      applyRequestRef.current += 1;
      resetPipeline();
      setAssignments([]);
      setNumericDrafts({});
      setApplying(false);
      setDragActive(false);
      setCadUnitsConfirmed(false);
      setCadOriginConfirmed(false);
      applyingRef.current = false;
      consumedInitialFiles.current = false;
    }
  }, [open, resetPipeline]);

  useEffect(() => {
    applyRequestRef.current += 1;
    applyingRef.current = false;
    setApplying(false);
    resetPipeline();
    setAssignments([]);
    setNumericDrafts({});
    setCadUnitsConfirmed(false);
    setCadOriginConfirmed(false);
  }, [resetPipeline, sessionKey]);

  useEffect(() => {
    if (!open) return undefined;
    previouslyFocusedRef.current = document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;
    const target = dialogRef.current?.querySelector<HTMLElement>(
      '[data-import-autofocus], [data-import-stage-autofocus]',
    )
      ?? dialogRef.current?.querySelector<HTMLElement>(
        'button:not([disabled]), input:not([disabled]), select:not([disabled])',
      );
    target?.focus();
    return () => {
      const previous = previouslyFocusedRef.current;
      if (previous?.isConnected) previous.focus();
      previouslyFocusedRef.current = null;
    };
  }, [open]);

  useEffect(() => {
    if (open && initialFiles && initialFiles.length > 0 && !consumedInitialFiles.current) {
      consumedInitialFiles.current = true;
      void processFiles(initialFiles);
    }
  }, [open, initialFiles, processFiles]);

  // Seed one suggested assignment per staged row.
  useEffect(() => {
    if (staged) {
      setAssignments(staged.rows.map(row => suggestImportRole(row)));
      setNumericDrafts({});
      setCadUnitsConfirmed(false);
      setCadOriginConfirmed(false);
    }
  }, [staged]);

  useEffect(() => {
    if (!open || !staged || dialogRef.current?.contains(document.activeElement)) return;
    dialogRef.current?.querySelector<HTMLElement>(
      '[data-import-stage-autofocus], select:not([disabled]), button:not([disabled])',
    )?.focus();
  }, [open, staged]);

  const unreferencedCadLayers = useMemo(() => (
    staged
      ? Array.from(staged.cadByRow.values()).filter(isUnreferencedCadLayer)
      : []
  ), [staged]);
  const requiresCadPlacementConfirmation = unreferencedCadLayers.length > 0;
  const drawingUnits = useMemo(() => (
    Array.from(new Set(unreferencedCadLayers.map(layer => (
      drawingUnitLabel(layer.attributes?.cadMetersPerUnit)
    ))))
  ), [unreferencedCadLayers]);

  const equipmentOptions = (testFit.plan?.rectangles ?? [])
    .filter(rectangle => rectangle.kind !== 'access-road' && !rectangle.id.startsWith('custom-'))
    .map(rectangle => ({
      id: rectangle.id,
      label: rectangle.label,
      kind: rectangle.kind,
      widthM: rectangle.widthM,
      heightM: rectangle.heightM,
    }));
  const occupancy = useMemo(
    () => computeGenerationEquipmentOccupancy(
      equipmentOptions.map(option => ({ id: option.id, kind: option.kind })),
      activeLayout.importedAssignments ?? [],
    ),
    [equipmentOptions, activeLayout.importedAssignments],
  );
  const occupancyByKind = useMemo(
    () => new Map(occupancy.map(entry => [entry.kind, entry])),
    [occupancy],
  );
  const assignmentErrors = useMemo(() => (
    staged?.rows
      .map((row, index) => assignmentDraftError(
        row,
        assignments[index] ?? { role: 'reference' },
        index,
        numericDrafts,
      ))
      .filter((error): error is string => error != null) ?? []
  ), [assignments, numericDrafts, staged]);
  const hasSourceErrors = rejections.length > 0
    || progress.some(row => row.status === 'error');

  // Equipment normalization preflight per row: a polygon assigned to modeled
  // equipment supplies pose only; the modeled envelope wins. When the source
  // dims differ by more than the confirm tolerance the row must be acknowledged.
  const equipmentPreflights = useMemo(() => {
    const map: Record<number, ReturnType<typeof equipmentNormalizationPreflight>> = {};
    staged?.rows.forEach((layer, index) => {
      const assignment = assignments[index];
      if (!assignment || assignment.role !== 'equipment' || layer.geometry.type !== 'polygon') return;
      const option = equipmentOptions.find(candidate => candidate.id === assignment.componentId);
      if (!option) return;
      map[index] = equipmentNormalizationPreflight(
        layer.geometry.coordinates,
        testFit.anchor,
        option.widthM,
        option.heightM,
      );
    });
    return map;
  }, [staged, assignments, equipmentOptions, testFit.anchor]);

  const pendingNormalization = useMemo(
    () => Object.entries(equipmentPreflights).some(
      ([index, preflight]) => preflight?.needsConfirmation && !confirmedNormalization[Number(index)],
    ),
    [equipmentPreflights, confirmedNormalization],
  );

  const setAssignment = useCallback((index: number, next: StudioImportRole) => {
    setAssignments(current => current.map((existing, i) => (i === index ? next : existing)));
  }, []);

  const setNumericAssignmentDraft = useCallback((
    key: string,
    value: string,
    commit: (numberValue: number | null) => void,
    minimum: number,
    maximum: number,
  ) => {
    setNumericDrafts(current => ({ ...current, [key]: value }));
    const numeric = Number(value);
    commit(
      value.trim() !== ''
        && Number.isFinite(numeric)
        && numeric >= minimum
        && numeric <= maximum
        ? numeric
        : null,
    );
  }, []);

  const processDroppedFiles = useCallback((files: File[]) => {
    if (files.length === 0 || parsing || applying) return;
    void processFiles(files);
  }, [applying, parsing, processFiles]);

  const handleDialogKeyDown = useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      if (!applying) onClose();
      return;
    }
    if (event.key !== 'Tab') return;
    const focusable = Array.from(
      dialogRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR) ?? [],
    );
    if (focusable.length === 0) {
      event.preventDefault();
      dialogRef.current?.focus();
      return;
    }
    const currentIndex = focusable.indexOf(document.activeElement as HTMLElement);
    const nextIndex = event.shiftKey
      ? (currentIndex <= 0 ? focusable.length - 1 : currentIndex - 1)
      : (currentIndex === -1 || currentIndex === focusable.length - 1 ? 0 : currentIndex + 1);
    if ((event.shiftKey && currentIndex <= 0)
      || (!event.shiftKey && (currentIndex === -1 || currentIndex === focusable.length - 1))) {
      event.preventDefault();
      focusable[nextIndex]?.focus();
    }
  }, [applying, onClose]);

  const apply = useCallback(async () => {
    if (!staged || applyingRef.current) return;
    if (hasSourceErrors || assignmentErrors.length > 0) {
      toast.error('Resolve every file and assignment error before applying this import.');
      return;
    }
    if (requiresCadPlacementConfirmation && (!cadUnitsConfirmed || !cadOriginConfirmed)) {
      toast.error('Confirm the unreferenced CAD drawing units and target-parcel origin before applying.');
      return;
    }
    const requestId = ++applyRequestRef.current;
    const requestSessionKey = sessionKey;
    const requestIsCurrent = (): boolean => (
      applyRequestRef.current === requestId
      && sessionKeyRef.current === requestSessionKey
    );
    applyingRef.current = true;
    setApplying(true);
    let referencesPersisted = false;
    let layoutCommitted = false;
    const addedReferenceIds = new Set<string>();
    try {
      if (!requestIsCurrent()) throw new Error('The active scenario changed before import started.');
      const preparedCadByRow = new Map<number, SiteLayer>();
      const rows: StudioImportRow[] = staged.rows.map((layer, index) => {
        const cadLayer = staged.cadByRow.get(index);
        const preparedCadLayer = cadLayer && isUnreferencedCadLayer(cadLayer)
          ? reanchorUnreferencedCadLayer(cadLayer, testFit.anchor)
          : cadLayer;
        if (preparedCadLayer) preparedCadByRow.set(index, preparedCadLayer);
        return {
          layer: preparedCadLayer
            ? {
                ...layer,
                geometry: preparedCadLayer.geometry,
                attributes: { ...layer.attributes, ...preparedCadLayer.attributes },
              }
            : layer,
          assignment: assignments[index] ?? { role: 'reference' },
        };
      });
      const importId = staged.provenance[0]?.importId;
      if (!importId) throw new Error('The import source fingerprint is unavailable.');
      const application = applyStudioImportAssignments(
        activeLayout,
        rows,
        testFit.anchor,
        importId,
      );

      for (const error of application.errors) toast.error(error);
      if (application.errors.length > 0) return;
      // Non-blocking advisories (e.g. near-square orientation ambiguity).
      for (const warning of application.warnings) toast.warning(warning);

      // Persist reference overlays before mutating the local layout. Awaiting
      // this write prevents the dialog from closing with a silently failed
      // Site-Map half of the import.
      if (application.referenceLayers.length > 0) {
        const imported = application.referenceLayers.map((layer, index) => {
          const rowIndex = application.referenceRowIndexes[index];
          const prebuilt = rowIndex == null ? undefined : preparedCadByRow.get(rowIndex);
          if (!prebuilt) return parsedToSiteLayer(layer);
          return {
            ...prebuilt,
            name: layer.name,
            category: layer.category,
            attributes: { ...prebuilt.attributes, ...layer.attributes },
          };
        });
        await updateSiteLayers(site.id, (currentLayers) => {
          const merge = mergeImportedLayers([...currentLayers], imported);
          for (const added of merge.added) addedReferenceIds.add(added.id);
          return preserveManagedGenerationImpactLayers([...currentLayers], merge.merged);
        });
        referencesPersisted = true;
        if (!requestIsCurrent()) {
          throw new Error('The active scenario changed while references were being saved.');
        }
      }

      if (application.patch) {
        if (!requestIsCurrent()) {
          throw new Error('The active scenario changed before the import could commit.');
        }
        const committed = await onPatchLayout(application.patch);
        if (committed === false) {
          throw new Error('The active scenario changed before the import could commit.');
        }
        // A true commit is terminal. Its own schema-v2 update changes the
        // input fingerprint, so checking the pre-commit scope after this
        // point would incorrectly classify success as stale.
        layoutCommitted = true;
      }

      // Archive originals best-effort — provenance never blocks the commit.
      for (const record of staged.provenance) {
        for (const file of record.files) {
          void uploadLayerImportProvenance(site.id, file, record.importId);
        }
      }

      const landed = staged.rows.length;
      if (landed > 0 && requestIsCurrent()) {
        toast.success(`${landed} imported ${landed === 1 ? 'feature' : 'features'} assigned.`);
      }
      if (requestIsCurrent()) onClose();
    } catch (error) {
      if (referencesPersisted && !layoutCommitted) {
        try {
          await updateSiteLayers(
            site.id,
            currentLayers => currentLayers.filter(layer => !addedReferenceIds.has(layer.id)),
          );
        } catch {
          // Preserve the original failure; the caller can retry after the
          // Site Map refreshes from persistence.
        }
      }
      if (requestIsCurrent()) {
        toast.error(
          error instanceof Error
            ? `Import could not be applied: ${error.message}`
            : 'Import could not be applied. Please retry.',
        );
      }
    } finally {
      if (requestIsCurrent()) {
        applyingRef.current = false;
        setApplying(false);
      }
    }
  }, [
    staged,
    requiresCadPlacementConfirmation,
    cadUnitsConfirmed,
    cadOriginConfirmed,
    assignments,
    activeLayout,
    testFit.anchor,
    onPatchLayout,
    site,
    updateSiteLayers,
    onClose,
    sessionKey,
    assignmentErrors,
    hasSourceErrors,
  ]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 p-4"
      data-testid="studio-import-dialog"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !applying) onClose();
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="studio-import-title"
        aria-describedby="studio-import-description"
        tabIndex={-1}
        onKeyDown={handleDialogKeyDown}
        className="flex max-h-[min(85dvh,52rem)] w-full max-w-2xl flex-col border border-border bg-card shadow-card outline-none rounded-md"
      >
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <div>
            <h2 id="studio-import-title" className="text-xs font-bold uppercase tracking-wider text-text-primary">
              Import to studio
            </h2>
            <p id="studio-import-description" className="mt-1 text-[10px] leading-relaxed text-text-muted">
              Review every feature and its engineering role before committing one atomic layout change.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={applying}
            aria-label="Close import dialog"
            className="flex h-11 w-11 shrink-0 items-center justify-center text-text-muted hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-40"
          >
            <X className="h-4 w-4" aria-hidden />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto p-4">
          {!staged && (
            <button
              type="button"
              data-import-autofocus
              onClick={() => fileInputRef.current?.click()}
              disabled={parsing}
              onDragEnter={(event) => {
                event.preventDefault();
                if (!parsing) setDragActive(true);
              }}
              onDragOver={(event) => {
                event.preventDefault();
                event.dataTransfer.dropEffect = 'copy';
                if (!parsing) setDragActive(true);
              }}
              onDragLeave={(event) => {
                event.preventDefault();
                if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
                  setDragActive(false);
                }
              }}
              onDrop={(event) => {
                event.preventDefault();
                event.stopPropagation();
                setDragActive(false);
                processDroppedFiles(Array.from(event.dataTransfer.files ?? []));
              }}
              aria-describedby="studio-import-drop-help"
              className={`flex min-h-44 w-full flex-col items-center justify-center gap-2 border border-dashed bg-background px-4 py-8 text-text-muted transition-colors hover:border-primary hover:text-text-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
                dragActive ? 'border-primary bg-primary/5 text-primary' : 'border-border'
              } rounded-md`}
              data-testid="studio-import-dropzone"
            >
              {parsing
                ? <Loader2 className="h-6 w-6 animate-spin" aria-hidden />
                : <UploadCloud className="h-6 w-6" aria-hidden />}
              <span className="text-xs font-bold uppercase tracking-wider">
                {parsing ? 'Parsing…' : dragActive ? 'Drop files to stage them' : 'Drop files or choose from device'}
              </span>
              <span id="studio-import-drop-help" className="text-[10px]">
                KMZ, KML, GeoJSON, zipped shapefile, or DWG · press Enter or Space to browse
              </span>
            </button>
          )}
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={GEO_IMPORT_ACCEPT}
            className="hidden"
            tabIndex={-1}
            aria-label="Choose geometry files to import"
            data-testid="studio-import-file-input"
            onChange={(event) => {
              const files = Array.from(event.target.files ?? []);
              event.target.value = '';
              void processFiles(files);
            }}
          />

          {rejections.map(rejection => (
            <p key={rejection} role="alert" className="mt-2 text-[10px] text-danger">{rejection}</p>
          ))}
          {progress.some(row => row.status === 'error') && progress
            .filter(row => row.status === 'error')
            .map(row => (
              <p key={row.plan.label} role="alert" className="mt-2 text-[10px] text-danger">{row.plan.label}: {row.error}</p>
            ))}

          {staged && (
            <div data-testid="studio-import-assignments">
              {staged.warnings.map(warning => (
                <p key={warning} role="status" className="mb-1 text-[10px] text-warning">{warning}</p>
              ))}
              {requiresCadPlacementConfirmation && (
                <fieldset
                  className="mb-3 border border-warning/60 bg-warning/5 p-3 rounded-lg"
                  aria-describedby="studio-cad-placement-help"
                  data-testid="studio-cad-placement-confirmation"
                >
                  <legend className="px-1 text-[10px] font-bold uppercase tracking-[0.14em] text-warning">
                    Unreferenced CAD placement
                  </legend>
                  <p id="studio-cad-placement-help" className="mb-2 text-[10px] leading-relaxed text-text-secondary">
                    This drawing has no supported embedded coordinate system. Confirm both assumptions before it can land.
                  </p>
                  <label className="flex min-h-11 cursor-pointer items-start gap-3 border-t border-warning/20 py-2 text-[10px] text-text-primary">
                    <input
                      type="checkbox"
                      checked={cadUnitsConfirmed}
                      onChange={(event) => setCadUnitsConfirmed(event.target.checked)}
                      className="mt-0.5 h-4 w-4 accent-primary"
                      data-import-stage-autofocus
                      data-testid="studio-cad-confirm-units"
                    />
                    <span>
                      <strong className="block font-semibold">Confirm drawing units</strong>
                      {drawingUnits.join(', ')}
                    </span>
                  </label>
                  <label className="flex min-h-11 cursor-pointer items-start gap-3 border-t border-warning/20 py-2 text-[10px] text-text-primary">
                    <input
                      type="checkbox"
                      checked={cadOriginConfirmed}
                      onChange={(event) => setCadOriginConfirmed(event.target.checked)}
                      className="mt-0.5 h-4 w-4 accent-primary"
                      data-testid="studio-cad-confirm-origin"
                    />
                    <span>
                      <strong className="block font-semibold">Place at target-parcel centroid</strong>
                      Origin {testFit.anchor.lat.toFixed(6)}, {testFit.anchor.lng.toFixed(6)}
                    </span>
                  </label>
                </fieldset>
              )}
              <table className="w-full text-left text-[10px]">
                <thead>
                  <tr className="border-b border-border text-[9px] uppercase text-text-muted">
                    <th className="py-1 pr-2">Feature</th>
                    <th className="py-1 pr-2">Geometry</th>
                    <th className="py-1">Role</th>
                  </tr>
                </thead>
                <tbody>
                  {staged.rows.map((row, index) => {
                    const assignment = assignments[index] ?? { role: 'reference' as const };
                    const available = rolesForGeometry(row.geometry.type);
                    const roleId = `studio-import-role-field-${index}`;
                    const barrierKindId = `studio-import-barrier-kind-${index}`;
                    const barrierHeightId = `studio-import-barrier-height-field-${index}`;
                    const customKindId = `studio-import-custom-kind-field-${index}`;
                    const roadWidthId = `studio-import-road-width-field-${index}`;
                    const equipmentId = `studio-import-equipment-field-${index}`;
                    const errorId = `studio-import-row-error-${index}`;
                    const barrierDraftKey = `barrier-height-${index}`;
                    const roadDraftKey = `road-width-${index}`;
                    const barrierDraft = numericDrafts[barrierDraftKey]
                      ?? (assignment.role === 'barrier' && assignment.heightM != null
                        ? String(assignment.heightM)
                        : '');
                    const roadDraft = numericDrafts[roadDraftKey]
                      ?? (assignment.role === 'custom' && assignment.corridorWidthM != null
                        ? String(assignment.corridorWidthM)
                        : '');
                    const rowError = assignmentDraftError(
                      row,
                      assignment,
                      index,
                      numericDrafts,
                    );
                    return (
                      <tr key={`${row.name}-${index}`} className="border-b border-border/60 align-top">
                        <td className="max-w-40 truncate py-1.5 pr-2 font-bold text-text-primary" title={row.name}>{row.name}</td>
                        <td className="py-1.5 pr-2 uppercase text-text-muted">{row.geometry.type}</td>
                        <td className="py-1.5">
                          <label htmlFor={roleId} className="sr-only">Role for {row.name}</label>
                          <select
                            id={roleId}
                            {...(index === 0 && !requiresCadPlacementConfirmation
                              ? { 'data-import-stage-autofocus': true }
                              : {})}
                            value={assignment.role}
                            onChange={(event) => {
                              const role = event.target.value as StudioImportRole['role'];
                              setNumericDrafts(current => ({
                                ...current,
                                [barrierDraftKey]: '',
                                [roadDraftKey]: '',
                              }));
                              setAssignment(index, role === 'barrier'
                                ? { role, kind: row.geometry.type === 'polygon' ? 'building' : 'wall', heightM: null }
                                : role === 'custom'
                                  ? { role, kind: row.geometry.type === 'line' ? 'road' : 'building', corridorWidthM: null }
                                  : role === 'equipment'
                                    ? { role, componentId: equipmentOptions[0]?.id ?? '' }
                                    : { role });
                            }}
                            aria-describedby={rowError ? errorId : undefined}
                            className="min-h-11 w-full border border-border bg-background px-2 py-1 text-[10px] text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-md"
                            data-testid={`studio-import-role-${index}`}
                          >
                            {available.map(role => (
                              <option key={role} value={role} disabled={role === 'equipment' && equipmentOptions.length === 0}>
                                {ROLE_LABELS[role]}
                              </option>
                            ))}
                          </select>
                          {assignment.role === 'barrier' && (
                            <div className="mt-2 grid grid-cols-2 gap-2">
                              <label htmlFor={barrierKindId} className="grid gap-1 text-[9px] font-semibold uppercase tracking-wider text-text-muted">
                                Barrier type
                                <select
                                  id={barrierKindId}
                                  value={assignment.kind}
                                  onChange={(event) => setAssignment(index, { ...assignment, kind: event.target.value as typeof assignment.kind })}
                                  className="min-h-11 border border-border bg-background px-2 text-[10px] normal-case tracking-normal text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-md"
                                >
                                  <option value="wall">Wall</option>
                                  <option value="berm">Berm</option>
                                  {row.geometry.type === 'polygon' && <option value="building">Building</option>}
                                </select>
                              </label>
                              <label htmlFor={barrierHeightId} className="grid gap-1 text-[9px] font-semibold uppercase tracking-wider text-text-muted">
                                <span>Height <span className="sr-only">in metres</span><span aria-hidden>(m)</span></span>
                                <input
                                  id={barrierHeightId}
                                  type="text"
                                  inputMode="decimal"
                                  value={barrierDraft}
                                  onChange={(event) => setNumericAssignmentDraft(
                                    barrierDraftKey,
                                    event.target.value,
                                    heightM => setAssignment(index, { ...assignment, heightM }),
                                    0.5,
                                    50,
                                  )}
                                  aria-invalid={rowError != null}
                                  aria-describedby={rowError ? errorId : undefined}
                                  className="min-h-11 w-full border border-border bg-background px-2 font-mono text-[10px] normal-case tracking-normal text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-md"
                                  data-testid={`studio-import-barrier-height-${index}`}
                                />
                              </label>
                            </div>
                          )}
                          {assignment.role === 'custom' && (
                            <div className="mt-2 grid grid-cols-2 gap-2">
                              <label htmlFor={customKindId} className="grid gap-1 text-[9px] font-semibold uppercase tracking-wider text-text-muted">
                                Component type
                                <select
                                  id={customKindId}
                                  value={assignment.kind}
                                  onChange={(event) => setAssignment(index, { ...assignment, kind: event.target.value as typeof assignment.kind })}
                                  className="min-h-11 border border-border bg-background px-2 text-[10px] normal-case tracking-normal text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-md"
                                  data-testid={`studio-import-custom-kind-${index}`}
                                >
                                  {(row.geometry.type === 'line'
                                    ? (['road'] as const)
                                    : (['road', 'parking', 'building', 'data-center', 'generator', 'custom'] as const)
                                  ).map(kind => <option key={kind} value={kind}>{kind}</option>)}
                                </select>
                              </label>
                              {(row.geometry.type === 'line' || assignment.kind === 'road') && row.geometry.type === 'line' && (
                                <label htmlFor={roadWidthId} className="grid gap-1 text-[9px] font-semibold uppercase tracking-wider text-text-muted">
                                  <span>Corridor width <span className="sr-only">in metres</span><span aria-hidden>(m)</span></span>
                                  <input
                                    id={roadWidthId}
                                    type="text"
                                    inputMode="decimal"
                                    value={roadDraft}
                                    onChange={(event) => setNumericAssignmentDraft(
                                      roadDraftKey,
                                      event.target.value,
                                      corridorWidthM => setAssignment(index, { ...assignment, corridorWidthM }),
                                      1,
                                      100,
                                    )}
                                    aria-invalid={rowError != null}
                                    aria-describedby={rowError ? errorId : undefined}
                                    className="min-h-11 w-full border border-border bg-background px-2 font-mono text-[10px] normal-case tracking-normal text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-md"
                                    data-testid={`studio-import-road-width-${index}`}
                                  />
                                </label>
                              )}
                            </div>
                          )}
                          {assignment.role === 'equipment' && (
                            <>
                              <label htmlFor={equipmentId} className="mt-2 grid gap-1 text-[9px] font-semibold uppercase tracking-wider text-text-muted">
                                Equipment to pin
                                <select
                                  id={equipmentId}
                                  value={assignment.componentId}
                                  onChange={(event) => setAssignment(index, { ...assignment, componentId: event.target.value })}
                                  aria-invalid={rowError != null}
                                  aria-describedby={rowError ? errorId : undefined}
                                  className="min-h-11 w-full border border-border bg-background px-2 text-[10px] normal-case tracking-normal text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-md"
                                  data-testid={`studio-import-equipment-${index}`}
                                >
                                  {equipmentOptions.map(option => (
                                    <option key={option.id} value={option.id}>{option.label}</option>
                                  ))}
                                </select>
                              </label>
                              {(() => {
                                const option = equipmentOptions.find(candidate => candidate.id === assignment.componentId);
                                const occ = option ? occupancyByKind.get(option.kind) : undefined;
                                if (!occ) return null;
                                const blocksFull = option?.kind === 'generation-block' && occ.available === 0;
                                return (
                                  <div className="mt-1">
                                    <p
                                      className="text-[9px] tabular-nums text-text-muted"
                                      data-testid={`studio-import-occupancy-${index}`}
                                    >
                                      {occ.required} required · {occ.assigned} assigned · {occ.available} available
                                    </p>
                                    {blocksFull && onAddGenerationBlock && (
                                      <button
                                        type="button"
                                        onClick={() => onAddGenerationBlock()}
                                        className="mt-1 min-h-9 border border-primary bg-primary/10 px-2 text-[9px] font-bold uppercase tracking-wider text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary max-[599px]:min-h-11 rounded-md"
                                        data-testid={`studio-import-add-block-${index}`}
                                        title="Grow the fleet by one block; assign this source to the new slot once it appears"
                                      >
                                        Add a generation block
                                      </button>
                                    )}
                                  </div>
                                );
                              })()}
                              {equipmentPreflights[index] && (() => {
                                const preflight = equipmentPreflights[index]!;
                                return (
                                  <div
                                    className="mt-2 border border-border bg-background/60 p-2 text-[9px] rounded-md"
                                    data-testid={`studio-import-preflight-${index}`}
                                  >
                                    <p className="font-semibold uppercase tracking-wider text-text-muted">Normalization — modeled dimensions win</p>
                                    <dl className="mt-1 grid grid-cols-[auto_1fr] gap-x-2 gap-y-0.5 tabular-nums text-text-secondary">
                                      <dt>Source</dt><dd>{preflight.sourceMajorM.toFixed(1)} × {preflight.sourceMinorM.toFixed(1)} m</dd>
                                      <dt>Modeled</dt><dd>{preflight.modeledLongM.toFixed(1)} × {preflight.modeledShortM.toFixed(1)} m</dd>
                                      <dt>Scale</dt><dd>{preflight.longScale.toFixed(2)}× / {preflight.shortScale.toFixed(2)}×</dd>
                                      <dt>Bearing</dt><dd>{preflight.nearSquare ? 'layout axis (near-square)' : `${preflight.bearingDeg.toFixed(0)}°`}</dd>
                                    </dl>
                                    {preflight.needsConfirmation && (
                                      <label className="mt-1.5 flex items-start gap-1.5 text-[10px] normal-case tracking-normal text-text-primary">
                                        <input
                                          type="checkbox"
                                          checked={!!confirmedNormalization[index]}
                                          onChange={(event) => setConfirmedNormalization(current => ({ ...current, [index]: event.target.checked }))}
                                          className="mt-0.5 h-4 w-4"
                                          data-testid={`studio-import-normalize-confirm-${index}`}
                                        />
                                        <span>Modeled envelope overrides the imported size (position &amp; orientation kept).</span>
                                      </label>
                                    )}
                                  </div>
                                );
                              })()}
                            </>
                          )}
                          {rowError && (
                            <p id={errorId} className="mt-1 text-[10px] leading-relaxed text-danger">
                              {rowError}
                            </p>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {staged && (
          <div className="flex items-center justify-between gap-2 border-t border-border px-4 py-3">
            <span className="text-[10px] text-text-muted">
              {staged.rows.length} feature{staged.rows.length === 1 ? '' : 's'} staged
              {staged.boundaryCandidateCount > 0 ? ` · ${staged.boundaryCandidateCount} boundary candidate${staged.boundaryCandidateCount === 1 ? '' : 's'} as overlays` : ''}
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => {
                  resetPipeline();
                  setAssignments([]);
                  setNumericDrafts({});
                  setCadUnitsConfirmed(false);
                  setCadOriginConfirmed(false);
                }}
                disabled={applying}
                className="min-h-11 border border-border px-3 py-2 text-[9px] font-bold uppercase tracking-wider text-text-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-md"
              >
                <FileUp className="mr-1 inline h-3 w-3" aria-hidden /> Different files
              </button>
              <button
                type="button"
                onClick={() => void apply()}
                disabled={
                  applying
                  || parsing
                  || staged.rows.length === 0
                  || hasSourceErrors
                  || assignmentErrors.length > 0
                  || pendingNormalization
                  || (requiresCadPlacementConfirmation
                    && (!cadUnitsConfirmed || !cadOriginConfirmed))
                }
                aria-describedby={requiresCadPlacementConfirmation ? 'studio-cad-placement-help' : undefined}
                className="min-h-11 border border-primary bg-primary/10 px-3 py-2 text-[9px] font-bold uppercase tracking-wider text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-40 rounded-md"
                data-testid="studio-import-apply"
              >
                {applying ? 'Applying…' : 'Apply assignments'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
